import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { downloadPdf } from './utils/downloadPdf';
import '../../styles/EvaluationForm.css';

const toJapaneseDate = (dateStr) => {
  try {
    return format(parseISO(dateStr), 'yyyy年MM月dd日');
  } catch {
    return '';
  }
};

// ✅ 安全にinput[type="date"]で扱えるよう整形する関数
const normalizeDateValue = (val) => {
  if (!val) return '';
  try {
    return format(new Date(val), 'yyyy-MM-dd');
  } catch {
    return '';
  }
};

const EvaluationForm = ({ loginCode, date, onSaved }) => {
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false); 

  const formattedDate = format(date, 'yyyy-MM-dd');
  const API_URL = 'https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport';

  useEffect(() => {
    const fetchEvaluation = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'get-weekly-evaluation',
            login_code: loginCode,
            date: formattedDate
          })
        });
        const data = await res.json();
        setRecord({
          ...data,
          evaluation_date: formattedDate
        });
      } catch (e) {
        alert('評価情報の取得に失敗しました');
      } finally {
        setIsLoading(false);
      }
    };
    fetchEvaluation();
  }, [loginCode, formattedDate]);

  const handleChange = (key, value) => {
    setRecord({ ...record, [key]: value });
  };

  const handleSave = async () => {
    try {
      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save-weekly-evaluation',
          login_code: loginCode,
          date: formattedDate,
          record: {
            prev_eval_date: record.prev_eval_date,
            period_start: record.period_start,
            period_end: record.period_end,
            evaluation_method: record.evaluation_method,
            method_other: record.method_other,
            evaluation_content: record.evaluation_content,
            recorder_name: record.recorder_name,
            confirm_name: record.confirm_name
          }
        })
      });
      alert('保存しました');
      if (onSaved) onSaved();
    } catch (e) {
      alert('保存に失敗しました');
    }
  };

    const handleGenerateAIContent = async () => {
    if (!record.period_start || !record.period_end) {
      alert('対象期間を先に入力してください');
      return;
    }

    setIsGenerating(true);
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-weekly-evaluation',
          login_code: loginCode,
          period_start: record.period_start,
          period_end: record.period_end
        })
      });

      const json = await res.json();
      const content = typeof json.body === 'string' ? JSON.parse(json.body).evaluation_content : json.body.evaluation_content;

      if (content) {
        setRecord(prev => ({ ...prev, evaluation_content: content }));
      } else {
        alert('AIからの評価文が取得できませんでした');
      }
    } catch (err) {
      console.error('AI生成エラー:', err);
      alert('AIによる評価文の生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  };

  if (isLoading) return <p>読み込み中...</p>;
  if (!record) return <p>データがありません</p>;

  return (
    <div className="evaluation-form">
      <h3>評価</h3>
      <table className="evaluation-table">
        <caption>※評価は作業・訓練対象日（期間の最終日）の1週間以内に実施する事</caption>
        <tbody>
          <tr>
            <th>対象者名</th>
            <td>{record.user_name}</td>
            <th>受給者証番号</th>
            <td>
              <input
                type="text"
                value={record.recipient_number || ''}
                disabled
                style={{ width: '100%' }}
              />
            </td>
          </tr>

          <tr>
            <th>実施日</th>
            <td>
              <input
                type="date"
                value={normalizeDateValue(record.evaluation_date)}
                onChange={(e) => handleChange('evaluation_date', e.target.value)}
                style={{ width: '100%' }}
              />
              <div>{toJapaneseDate(record.evaluation_date)}</div>
            </td>
            <th>前回評価日</th>
            <td>
              <input
                type="date"
                value={normalizeDateValue(record.prev_eval_date)}
                onChange={(e) => handleChange('prev_eval_date', e.target.value)}
                style={{ width: '100%' }}
              />
              <div>{record.prev_eval_display || toJapaneseDate(record.prev_eval_date)}</div>
            </td>
          </tr>

          <tr>
            <th>対象期間</th>
            <td colSpan="3">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="date"
                  value={normalizeDateValue(record.period_start)}
                  onChange={(e) => handleChange('period_start', e.target.value)}
                />
                ～ 
                <input
                  type="date"
                  value={normalizeDateValue(record.period_end)}
                  onChange={(e) => handleChange('period_end', e.target.value)}
                />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  （評価は1週間ごとに実施）
                </span>
              </div>
            </td>
          </tr>

          <tr>
            <th>評価方法</th>
            <td colSpan="3">
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="evaluation_method"
                    value="通所"
                    checked={record.evaluation_method === '通所'}
                    onChange={(e) => handleChange('evaluation_method', e.target.value)}
                  />
                  通所
                </label>
                <label>
                  <input
                    type="radio"
                    name="evaluation_method"
                    value="訪問"
                    checked={record.evaluation_method === '訪問'}
                    onChange={(e) => handleChange('evaluation_method', e.target.value)}
                  />
                  訪問
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="radio"
                    name="evaluation_method"
                    value="その他"
                    checked={record.evaluation_method === 'その他'}
                    onChange={(e) => handleChange('evaluation_method', e.target.value)}
                  />
                  その他（
                  <input
                    type="text"
                    value={record.method_other || ''}
                    onChange={(e) => handleChange('method_other', e.target.value)}
                    disabled={record.evaluation_method !== 'その他'}
                    style={{ width: '160px' }}
                  />
                  ）
                </label>
              </div>
            </td>
          </tr>

          <tr>
            <td colSpan="4">
              <div className="evaluation-label-with-button">
                <strong>評価内容</strong>
                <button
                  className="custom-button ai-generate-button"
                  onClick={handleGenerateAIContent}
                  disabled={isGenerating}
                >
                  {isGenerating ? '生成中...' : 'AIで生成'}
                </button>
              </div>
              <textarea
                rows={10}
                value={record.evaluation_content || ''}
                onChange={(e) => handleChange('evaluation_content', e.target.value)}
                style={{ width: '100%' }}
              />
            </td>
          </tr>

          <tr>
            <th>記録者名</th>
            <td>
              <input
                type="text"
                value={record.recorder_name || ''}
                onChange={(e) => handleChange('recorder_name', e.target.value)}
                style={{ width: '100%' }}
              />
            </td>
            <th>確認者名（サービス管理責任者）</th>
            <td>
              <input
                type="text"
                value={record.confirm_name || ''}
                onChange={(e) => handleChange('confirm_name', e.target.value)}
                style={{ width: '100%' }}
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: '1.5rem' }}>
        <button className="custom-button" onClick={handleSave}>保存</button>
        <button
          className="custom-button"
          onClick={() => downloadPdf({
            apiUrl: 'https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport',
            action: 'get-weekly-evaluation-pdf',
            loginCode,
            date: formattedDate,
            fallbackFilename: 'Weekly-Evaluation.pdf'
          })}
        >
          PDFダウンロード
        </button>
      </div>
    </div>
  );
};

export default EvaluationForm;
